import tag from '@123/druid/dist/Framework/Decorators/Tag';
import type {PickupLocation} from '@Component/PickupLocator/Schema/PickupLocation';
import Geocoder from '@Service/Geocoder/Geocoder';
import GoogleAutocompleteUI from '@Component/Google/Autocomplete/GoogleAutocompleteUI';
import '@Component/Google/Autocomplete/GoogleAutocompleteStyle';
import type StringMap from '@123/druid/dist/Types/StringMap';

@tag('dr-google-autocomplete')
export default class GoogleAutocomplete extends HTMLElement {
    public autocomplete?: google.maps.places.PlaceAutocompleteElement;
    declare private ui: GoogleAutocompleteUI;
    private geocoder?: Geocoder;

    get key(): string {
        return this.attributeMap().getString('key', '');
    }

    get countries(): StringMap {
        return this.attributeMap().getJson('countries', []);
    }

    get value(): string {
        return this.ui.input.value;
    }

    set value(value: string) {
        this.ui.input.value = value;
        void this.onAutocompleteChange();
    }

    public connectedCallback(): void {
        this.geocoder = new Geocoder();
        this.geocoder
            .setup()
            .then(() => {
                this.setupGoogle()
                    .then(() => {
                        this.ui = new GoogleAutocompleteUI(this);

                        // @ts-expect-error No EventMap available
                        this.autocomplete.addEventListener('gmp-select', async ({placePrediction}) => this.onAutocompleteSelect(placePrediction));
                        this.ui.clearButton.addEventListener('click', () => this.dispatchEvent(new CustomEvent('clear', {bubbles: true})));

                        this.dispatchEvent(new CustomEvent('connected', {bubbles: true}));
                        this.value = this.attributeMap().getString('initial-value', '');
                    })
                    .catch((e) => console.error('Failed to setup Google Autocomplete', e));
            })
            .catch(() => {});
    }

    public disconnectedCallback(): void {
        if (this.autocomplete === undefined) {
            return;
        }

        this.geocoder = undefined;
        this.autocomplete.removeEventListeners('gmp-select');
        this.ui.clearButton.removeEventListeners('click');
        this.autocomplete.remove();

        this.dispatchEvent(new CustomEvent('disconnected', {bubbles: true}));
    }

    private async setupGoogle(): Promise<void> {
        const {setOptions, importLibrary} = await import('@googlemaps/js-api-loader');

        setOptions({key: this.key});
        await importLibrary('places');

        // @ts-expect-error includedRegionCodes is a valid key
        this.autocomplete = new google.maps.places.PlaceAutocompleteElement({includedRegionCodes: this.countries});
        this.appendChild(this.autocomplete);
    }

    private async onAutocompleteChange(): Promise<void> {
        if (this.geocoder === undefined) {
            return;
        }

        this.geocoder
            .provide(this.value.trim())
            .then((location) => {
                this.dispatchEvent(new CustomEvent<PickupLocation>('located', {bubbles: true, detail: location}));
            })
            .catch(() => {});
    }

    private async onAutocompleteSelect(placePrediction: google.maps.places.PlacePrediction): Promise<void> {
        if (this.autocomplete === undefined) {
            return;
        }

        const place = placePrediction.toPlace();
        await place.fetchFields({fields: ['location', 'addressComponents']});

        if (place.location === undefined || place.location === null) {
            return;
        }

        this.dispatchEvent(
            new CustomEvent<PickupLocation>('located', {
                bubbles: true,
                detail: {
                    lat: place.location.lat(),
                    lng: place.location.lng(),
                    country: this.getCountry(place),
                    id: null,
                    icon: null,
                },
            }),
        );
    }

    private getCountry(place: google.maps.places.Place): string | null {
        return place.addressComponents?.find((component) => component.types.includes('country'))?.shortText ?? null;
    }
}
